/**
 * Data layer for `affiliate-notes`. Thin typed fetch wrappers over
 * /api/v1/affiliate-notes. Raw `fetch()` is confined to this file per
 * the dash contract (D6).
 *
 * Backend endpoints:
 *   GET    /notes/:affiliate_id   -> list notes for affiliate
 *   POST   /notes                 -> create note {affiliate_id, body, pinned?}
 *   DELETE /notes/:id             -> delete note
 *   GET    /tags/:affiliate_id    -> list tags for affiliate
 *   POST   /tags                  -> upsert tag {affiliate_id, tag, color?}
 *   DELETE /tags/:id              -> delete tag
 */

export type TagColor =
	| "zinc"
	| "red"
	| "amber"
	| "emerald"
	| "sky"
	| "violet"
	| "rose";

export const TAG_COLORS: readonly TagColor[] = [
	"zinc",
	"red",
	"amber",
	"emerald",
	"sky",
	"violet",
	"rose",
] as const;

export interface AffiliateNoteRow {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	author_id: string | null;
	body: string;
	pinned: boolean;
	created_at: number;
}

export interface AffiliateTagRow {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	tag: string;
	color: string;
	created_at: number;
}

export interface ListNotesResponse {
	affiliate_id: string;
	count: number;
	items: AffiliateNoteRow[];
}

export interface ListTagsResponse {
	affiliate_id: string;
	count: number;
	items: AffiliateTagRow[];
}

export interface CreateNoteBody {
	affiliate_id: string;
	body: string;
	pinned?: boolean;
}

export interface CreateTagBody {
	affiliate_id: string;
	tag: string;
	color?: TagColor;
}

const BASE = "/api/v1/affiliate-notes";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// notes
// ---------------------------------------------------------------------------

export async function listNotes(affiliateId: string): Promise<ListNotesResponse> {
	const res = await fetch(`${BASE}/notes/${encodeURIComponent(affiliateId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListNotesResponse>(res);
}

export async function createNote(body: CreateNoteBody): Promise<AffiliateNoteRow> {
	const res = await fetch(`${BASE}/notes`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AffiliateNoteRow>(res);
}

export async function deleteNote(id: string): Promise<{ id: string; deleted: boolean }> {
	const res = await fetch(`${BASE}/notes/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ id: string; deleted: boolean }>(res);
}

// ---------------------------------------------------------------------------
// tags
// ---------------------------------------------------------------------------

export async function listTags(affiliateId: string): Promise<ListTagsResponse> {
	const res = await fetch(`${BASE}/tags/${encodeURIComponent(affiliateId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListTagsResponse>(res);
}

export async function createTag(body: CreateTagBody): Promise<AffiliateTagRow> {
	const res = await fetch(`${BASE}/tags`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AffiliateTagRow>(res);
}

export async function deleteTag(id: string): Promise<{ id: string; deleted: boolean }> {
	const res = await fetch(`${BASE}/tags/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ id: string; deleted: boolean }>(res);
}

// ---------------------------------------------------------------------------
// query keys
// ---------------------------------------------------------------------------

export const affiliateNotesQueryKeys = {
	all: ["affiliate-notes"] as const,
	notes: (affiliateId: string) =>
		["affiliate-notes", "notes", affiliateId] as const,
	tags: (affiliateId: string) =>
		["affiliate-notes", "tags", affiliateId] as const,
};
